import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useThemeColors } from '../context/ThemeContext';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { zLayer } from '../styles/zIndex';
import { isPushSupported, pushPermission, enablePush, listenForegroundPush, listenPushClickNav } from '../utils/push';
import PushBlockedModal from './PushBlockedModal';

const DISMISS_KEY = 'pushPromptDismissed';

// ─── POR QUÉ SOLO EN ESTAS DOS RUTAS ────────────────────────────────────────
//
// El aviso es `position: fixed` a 84px del fondo, y esos 84px asumen que abajo
// solo está el BottomNav. No es cierto: varias pantallas ponen su PROPIA barra
// de acción en esa misma franja. Medido con `elementFromPoint` en /checkout a
// 390×844: el toque sobre «Siguiente» lo recibía el botón «Sí, activar» de este
// aviso, y el de «Capturar ubicación de entrega» lo recibía su párrafo. Le sale
// a TODA cuenta nueva —el aviso aparece por defecto— y le corta el paso en su
// primera compra. Las mismas barras están en /product/:id y /diagnose.
//
// La capa (`zLayer.optionalPrompt`, por debajo de `zLayer.actionBar`) deja el
// orden correcto escrito y sujeto por prueba. Pero NO alcanza sola: en
// escritorio el `<main>` de App.js abre un contexto de apilamiento (F-4) y la
// barra de la página queda encerrada bajo z=1 pida lo que pida, mientras este
// aviso compite a nivel raíz. Por eso la defensa principal es no pintarse donde
// hay barra: sin eso, en escritorio el aviso volvería a ganar aunque su número
// sea menor.
//
// La lista es BLANCA y no negra a propósito, igual que en `VerifyEmailBanner`:
// enumerar las pantallas conflictivas es una carrera que se pierde, porque cada
// barra fija nueva vuelve a romperlo en silencio.
//
// Las dos elegidas: `/home` es el aterrizaje después de entrar, así que lo ve
// todo el mundo; e `/inbox` es la pantalla de las notificaciones, donde pedirlas
// es lo más pertinente que se puede hacer. Ninguna de las dos tiene barra fija
// inferior (comprobado en su fuente: solo cabeceras `sticky` arriba).
//
// ⚠️ Antes de añadir una ruta: comprobar que EXISTE en App.js y medir con
// `elementFromPoint`, en móvil Y en escritorio, que no deja ningún control
// debajo. Sin medirlo, no se añade. El control permanente vive en EditProfile,
// así que nadie se queda sin poder activarlas.
const RUTAS_CON_AVISO = ['/home', '/inbox'];

/**
 * Banner suave que invita a activar notificaciones push una sola vez.
 * Solo aparece si el navegador lo soporta, el permiso está sin decidir ('default'),
 * el usuario no lo activó antes y no lo descartó. El control permanente vive en EditProfile.
 */
export default function PushPrompt() {
  const { user, userProfile } = useAuth();
  const location = useLocation();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useThemeColors();
  const T = isDark ? dsv2 : dsv2Light;

  // Al tocar una notificación con la app abierta, el SW de FCM manda un mensaje
  // y navegamos por dentro de la SPA (navigate() del SW está prohibido).
  useEffect(() => listenPushClickNav((path) => navigate(path)), [navigate]);

  const [show, setShow] = useState(false);
  const [working, setWorking] = useState(false);
  const [blocked, setBlocked] = useState(false);

  // Toast cuando llega un push con la app en primer plano (en background lo muestra el SW).
  useEffect(() => {
    const cleanup = listenForegroundPush((payload) => {
      // Mensajes DATA-ONLY: el contenido viene en payload.data (no en .notification).
      const d = payload?.data || payload?.notification || {};
      const title = d.title;
      if (!title) return;
      const link = d.link;
      showToast(
        title,
        'info',
        link ? { label: t('push.view'), onClick: () => { window.location.href = link; } } : null
      );
    });
    return cleanup;
  }, [showToast, t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userProfile?.id) return;
      if (localStorage.getItem(DISMISS_KEY)) return;
      if (localStorage.getItem('fcmToken')) return;
      if (pushPermission() !== 'default') return;
      if (!(await isPushSupported())) return;
      if (!cancelled) setShow(true);
    })();
    return () => { cancelled = true; };
  }, [userProfile]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  const activate = async () => {
    setWorking(true);
    const res = await enablePush(userProfile.id);
    setWorking(false);
    if (res.ok) {
      // Solo descartar permanentemente cuando push quedó realmente activado.
      localStorage.setItem(DISMISS_KEY, '1');
      setShow(false);
      showToast(t('push.enabled'), 'success');
    } else if (res.reason === 'denied') {
      // Permiso bloqueado: mostrar modal explicativo, no descartar permanentemente.
      setShow(false);
      setBlocked(true);
    }
    // error / unsupported: no descartar — dejar el banner para reintentar.
  };

  // Un aviso a la vez, y el del correo primero. `VerifyEmailBanner` ocupa la
  // misma franja (bottom 92px) en /home, así que los dos juntos se pisan; y
  // pedirle permiso de notificaciones a quien todavía no confirmó su correo es
  // pedir dos cosas a la vez a alguien que acaba de llegar.
  const faltaVerificarCorreo = !!user && user.emailVerified === false && !userProfile?.emailVerified;
  const rutaPermitida = RUTAS_CON_AVISO.includes(location.pathname);

  if (!show || !rutaPermitida || faltaVerificarCorreo) {
    return <PushBlockedModal open={blocked} onClose={() => setBlocked(false)} />;
  }

  return (
    <>
    <PushBlockedModal open={blocked} onClose={() => setBlocked(false)} />
    <div style={{
      position: 'fixed', bottom: '84px', left: '12px', right: '12px', zIndex: zLayer.optionalPrompt,
      maxWidth: '480px', margin: '0 auto',
      backgroundColor: T.surface, border: `1px solid ${T.outline}`,
      borderRadius: T.radius.xl, padding: '16px',
      boxShadow: T.shadow.lg, display: 'flex', gap: '12px', alignItems: 'flex-start',
    }}>
      <div style={{
        width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
        backgroundColor: T.red, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Bell size={20} color="white" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: T.text, marginBottom: '2px' }}>
          {t('push.promptTitle')}
        </div>
        <p style={{ fontSize: '13px', color: T.textMuted, margin: '0 0 12px', lineHeight: 1.4 }}>
          {t('push.promptBody')}
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={activate}
            disabled={working}
            style={{
              flex: 1, padding: '10px', backgroundColor: T.red, color: 'white', border: 'none',
              borderRadius: T.radius.md, fontWeight: '700', fontSize: '14px', cursor: 'pointer',
            }}
          >
            {working ? '...' : t('push.enable')}
          </button>
          <button
            onClick={dismiss}
            style={{
              padding: '10px 16px', backgroundColor: T.surfaceLow, color: T.textMuted, border: 'none',
              borderRadius: T.radius.md, fontWeight: '600', fontSize: '14px', cursor: 'pointer',
            }}
          >
            {t('push.later')}
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        aria-label={t('push.later')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, padding: 0 }}
      >
        <X size={18} />
      </button>
    </div>
    </>
  );
}
